import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import {
    CheckCircleRounded,
    CloseRounded,
    ContentCopyRounded,
    DoneAllRounded,
    SendRounded,
} from "@mui/icons-material";
import {
    Box,
    CircularProgress,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import { colors } from "../../theme/colors";
import { toast } from "../../universal/toast";
import utils from "../../universal/utils";
import { resolveUserAvatarSrc } from "../../utils/avatarUrl";
import {
    buildSupportTicketTimeline,
    formatSupportTicketCategory,
    formatSupportTicketDay,
    formatSupportTicketRelativeTime,
    formatSupportTicketTime,
    getSupportTicketStatusLabel,
    getSupportTicketStatusTone,
} from "./supportTicketUi";
import {
    SUPPORT_TICKET_CHAT_CLOSE_BUTTON_SX,
    SUPPORT_TICKET_CHAT_EYEBROW_SX,
    SUPPORT_TICKET_CHAT_HEADER_SX,
    SUPPORT_TICKET_CHAT_HEADER_BRAND_SX,
    SUPPORT_TICKET_CHAT_HEADER_LOGO_SX,
    SUPPORT_TICKET_CHAT_HEADER_MAIN_SX,
    SUPPORT_TICKET_CHAT_HEADER_META_PANEL_SX,
    SUPPORT_TICKET_CHAT_HEADER_TOP_SX,
    SUPPORT_TICKET_CHAT_META_SX,
    SUPPORT_TICKET_CHAT_SHELL_SX,
    SUPPORT_TICKET_CHAT_TITLE_SX,
    SUPPORT_TICKET_CLOSED_SX,
    SUPPORT_TICKET_COMPOSER_FIELD_SX,
    SUPPORT_TICKET_COMPOSER_SX,
    SUPPORT_TICKET_CONVERSATION_SX,
    SUPPORT_TICKET_COPY_ID_BUTTON_SX,
    SUPPORT_TICKET_DATE_DIVIDER_SX,
    SUPPORT_TICKET_EVENT_SX,
    SUPPORT_TICKET_MESSAGE_ATTACHMENT_NAME_SX,
    SUPPORT_TICKET_MESSAGE_ATTACHMENTS_SX,
    SUPPORT_TICKET_MESSAGE_IMAGE_SX,
    SUPPORT_TICKET_MESSAGE_META_SX,
    SUPPORT_TICKET_MESSAGE_SENT_SX,
    SUPPORT_TICKET_MESSAGE_TEXT_SX,
    SUPPORT_TICKET_META_ITEM_SX,
    SUPPORT_TICKET_PLAYER_AVATAR_SX,
    SUPPORT_TICKET_SEND_BUTTON_SX,
    SUPPORT_TICKET_STATE_COPY_SX,
    SUPPORT_TICKET_STATE_SX,
    SUPPORT_TICKET_STATE_TITLE_SX,
    SUPPORT_TICKET_SUPPORT_AVATAR_SX,
    getSupportTicketMessageBubbleSx,
    getSupportTicketMessageRowSx,
    getSupportTicketStatusSx,
} from "./supportTicketsPageStyles";

const OURO_LOGO_URL =
    "https://ouro-public-assets.s3.eu-west-2.amazonaws.com/init-logo-darkmode.png";
const basePath = utils.getEdgeBasePath();

function getAttachmentPreviewUrl(attachment) {
    if (!attachment || typeof attachment !== "object") return "";
    return attachment.dataUrl ||
        attachment.previewUrl ||
        attachment.url ||
        attachment.href ||
        attachment.downloadUrl ||
        attachment.src ||
        "";
}

function isImageAttachment(attachment) {
    const type = String(attachment?.type || attachment?.mimeType || "");
    const name = String(attachment?.name || attachment?.filename || "");
    const url = getAttachmentPreviewUrl(attachment);
    return type.startsWith("image/") ||
        /\.(png|jpe?g|gif|webp|avif)$/i.test(name) ||
        /^data:image\//i.test(url);
}

function copyTextToClipboard(text) {
    if (typeof navigator !== "undefined" && navigator?.clipboard?.writeText) {
        return navigator.clipboard.writeText(text);
    }

    if (typeof document === "undefined") {
        return Promise.reject(new Error("Copy unavailable"));
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    if (!ok) throw new Error("Copy failed");
    return Promise.resolve();
}

function SupportAvatar() {
    return (
        <Box aria-hidden sx={SUPPORT_TICKET_SUPPORT_AVATAR_SX}>
            <Box component="img" src={OURO_LOGO_URL} alt="" />
        </Box>
    );
}

function PlayerAvatar({ user, avatarCacheBust }) {
    const avatarSrc = resolveUserAvatarSrc({
        basePath,
        user,
        avatarCacheBust,
    });
    const fallback = String(user?.nickname || user?.name || user?.email || "Y")
        .trim()
        .slice(0, 1)
        .toUpperCase();

    return (
        <Box aria-hidden sx={SUPPORT_TICKET_PLAYER_AVATAR_SX}>
            {avatarSrc ? (
                <Box component="img" src={avatarSrc} alt="" />
            ) : (
                fallback || "Y"
            )}
        </Box>
    );
}

PlayerAvatar.propTypes = {
    user: PropTypes.object,
    avatarCacheBust: PropTypes.number,
};

function MessageAttachments({ attachments = [] }) {
    const visibleAttachments = attachments.filter(Boolean);
    if (!visibleAttachments.length) return null;

    return (
        <Box sx={SUPPORT_TICKET_MESSAGE_ATTACHMENTS_SX}>
            {visibleAttachments.map((attachment, index) => {
                const name = attachment?.name || attachment?.filename || "Attachment";
                const previewUrl = getAttachmentPreviewUrl(attachment);
                const showImage = previewUrl && isImageAttachment(attachment);

                return showImage ? (
                    <Box
                        key={`${name}-${index}`}
                        component="img"
                        src={previewUrl}
                        alt={name}
                        sx={SUPPORT_TICKET_MESSAGE_IMAGE_SX}
                    />
                ) : (
                    <Box
                        key={`${name}-${index}`}
                        sx={SUPPORT_TICKET_MESSAGE_ATTACHMENT_NAME_SX}
                    >
                        {name}
                    </Box>
                );
            })}
        </Box>
    );
}

MessageAttachments.propTypes = {
    attachments: PropTypes.array,
};

export default function SupportTicketSidebarChat({
    ticket,
    loading,
    error,
    replyContent,
    replying,
    user,
    avatarCacheBust,
    onReplyChange,
    onSendReply,
    onBack,
}) {
    const conversationRef = useRef(null);
    const timeline = useMemo(
        () => buildSupportTicketTimeline(ticket),
        [ticket],
    );

    useEffect(() => {
        if (!conversationRef.current || !ticket) return;
        conversationRef.current.scrollTop =
            conversationRef.current.scrollHeight;
    }, [ticket]);

    if (loading) {
        return (
            <Box sx={SUPPORT_TICKET_STATE_SX}>
                <CircularProgress size={28} sx={{ color: colors.primary.main }} />
                <Typography sx={SUPPORT_TICKET_STATE_COPY_SX}>
                    Loading your conversation...
                </Typography>
            </Box>
        );
    }

    if (error || !ticket) {
        return (
            <Box sx={SUPPORT_TICKET_STATE_SX}>
                <Typography sx={SUPPORT_TICKET_STATE_TITLE_SX}>
                    Ticket unavailable
                </Typography>
                <Typography sx={SUPPORT_TICKET_STATE_COPY_SX}>
                    {error || "This ticket could not be found."}
                </Typography>
                <Box
                    component="button"
                    type="button"
                    onClick={onBack}
                    sx={{
                        mt: "0.35rem",
                        px: "0.9rem",
                        py: "0.55rem",
                        color: colors.accent.soft,
                        border: `1px solid ${colors.border.default}`,
                        borderRadius: "9px",
                        background: "rgba(255,255,255,0.025)",
                        font: "inherit",
                        fontSize: "0.74rem",
                        fontWeight: 700,
                        cursor: "pointer",
                    }}
                >
                    Back to tickets
                </Box>
            </Box>
        );
    }

    const statusTone = getSupportTicketStatusTone(ticket.status);
    const isClosed =
        ticket.status === "closed" || ticket.status === "resolved";

    const handleKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            onSendReply();
        }
    };

    const handleCopyTicketId = async () => {
        const ticketId = ticket.ticketNumber || ticket._id;
        try {
            await copyTextToClipboard(ticketId);
            toast.success("Ticket ID copied successfully");
        } catch (copyError) {
            toast.error("Ticket ID could not be copied");
        }
    };

    return (
        <Box component="section" aria-label="Support ticket conversation" sx={SUPPORT_TICKET_CHAT_SHELL_SX}>
            <Box component="header" sx={SUPPORT_TICKET_CHAT_HEADER_SX}>
                <Box sx={SUPPORT_TICKET_CHAT_HEADER_TOP_SX}>
                    <Box sx={SUPPORT_TICKET_CHAT_HEADER_BRAND_SX}>
                        <Box
                            component="img"
                            src={OURO_LOGO_URL}
                            alt=""
                            sx={SUPPORT_TICKET_CHAT_HEADER_LOGO_SX}
                        />
                        <Typography component="span" sx={SUPPORT_TICKET_CHAT_EYEBROW_SX}>
                            Ouro Support
                        </Typography>
                    </Box>

                    <IconButton
                        onClick={onBack}
                        aria-label="Close support ticket"
                        sx={SUPPORT_TICKET_CHAT_CLOSE_BUTTON_SX}
                    >
                        <CloseRounded fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={SUPPORT_TICKET_CHAT_HEADER_MAIN_SX}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography component="h1" sx={SUPPORT_TICKET_CHAT_TITLE_SX}>
                            {ticket.subject || "Support ticket"}
                        </Typography>
                    </Box>
                    <Box
                        component="span"
                        sx={getSupportTicketStatusSx(statusTone)}
                    >
                        {getSupportTicketStatusLabel(ticket.status)}
                    </Box>
                </Box>

                <Box sx={SUPPORT_TICKET_CHAT_HEADER_META_PANEL_SX}>
                    <Box sx={SUPPORT_TICKET_CHAT_META_SX}>
                        <Box component="span" sx={SUPPORT_TICKET_META_ITEM_SX}>
                            <Box component="span">{"#" + ticket.ticketNumber}</Box>
                            <IconButton
                                aria-label="Copy ticket ID"
                                onClick={handleCopyTicketId}
                                sx={SUPPORT_TICKET_COPY_ID_BUTTON_SX}
                            >
                                <ContentCopyRounded />
                            </IconButton>
                        </Box>
                        <Box component="span" sx={SUPPORT_TICKET_META_ITEM_SX}>
                            {formatSupportTicketCategory(ticket.category)}
                        </Box>
                        <Box component="span" sx={SUPPORT_TICKET_META_ITEM_SX}>
                            {"Updated " +
                                formatSupportTicketRelativeTime(
                                    ticket.updatedAt || ticket.createdAt,
                                )}
                        </Box>
                        {ticket.priority === "high" ? (
                            <Box
                                component="span"
                                sx={{
                                    ...SUPPORT_TICKET_META_ITEM_SX,
                                    color: colors.state.danger.main,
                                }}
                            >
                                High priority
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            </Box>

            <Box ref={conversationRef} sx={SUPPORT_TICKET_CONVERSATION_SX}>
                {timeline.length === 0 ? (
                    <Box sx={{ py: "4rem", textAlign: "center" }}>
                        <Typography sx={SUPPORT_TICKET_STATE_COPY_SX}>
                            Your conversation will appear here.
                        </Typography>
                    </Box>
                ) : null}

                {timeline.map((item, index) => {
                    const previous = timeline[index - 1];
                    const showDay =
                        !previous ||
                        formatSupportTicketDay(previous.timestamp) !==
                            formatSupportTicketDay(item.timestamp);

                    if (item.type === "event") {
                        return (
                            <React.Fragment key={item.key}>
                                {showDay ? (
                                    <Box sx={SUPPORT_TICKET_DATE_DIVIDER_SX}>
                                        {formatSupportTicketDay(item.timestamp)}
                                    </Box>
                                ) : null}
                                <Box sx={SUPPORT_TICKET_EVENT_SX}>
                                    <Box component="span">
                                        <CheckCircleRounded />
                                        {"Ticket marked " +
                                            getSupportTicketStatusLabel(item.to) +
                                            " - " +
                                            formatSupportTicketTime(item.timestamp)}
                                    </Box>
                                </Box>
                            </React.Fragment>
                        );
                    }

                    const fromSupport = item.author === "admin";
                    return (
                        <React.Fragment key={item.key}>
                            {showDay ? (
                                <Box sx={SUPPORT_TICKET_DATE_DIVIDER_SX}>
                                    {formatSupportTicketDay(item.timestamp)}
                                </Box>
                            ) : null}
                            <Box sx={getSupportTicketMessageRowSx(fromSupport)}>
                                {fromSupport ? <SupportAvatar /> : null}

                                <Box sx={getSupportTicketMessageBubbleSx(fromSupport)}>
                                    <Box sx={SUPPORT_TICKET_MESSAGE_META_SX}>
                                        <Box component="span">
                                            {fromSupport
                                                ? item.authorName || "Ouro Support"
                                                : "You"}
                                        </Box>
                                        <Box component="span">-</Box>
                                        <Box component="span">
                                            {formatSupportTicketTime(item.timestamp)}
                                        </Box>
                                        {item.initial ? (
                                            <Box component="span">- Ticket created</Box>
                                        ) : null}
                                    </Box>
                                    {item.content ? (
                                        <Typography sx={SUPPORT_TICKET_MESSAGE_TEXT_SX}>
                                            {item.content}
                                        </Typography>
                                    ) : null}
                                    <MessageAttachments attachments={item.attachments} />
                                    {!fromSupport ? (
                                        <DoneAllRounded
                                            aria-label="Message sent"
                                            sx={SUPPORT_TICKET_MESSAGE_SENT_SX}
                                        />
                                    ) : null}
                                </Box>

                                {!fromSupport ? (
                                    <PlayerAvatar
                                        user={user}
                                        avatarCacheBust={avatarCacheBust}
                                    />
                                ) : null}
                            </Box>
                        </React.Fragment>
                    );
                })}
            </Box>

            {isClosed ? (
                <Box sx={SUPPORT_TICKET_CLOSED_SX}>
                    <CheckCircleRounded />
                    This conversation is closed. Create a new ticket if you need more help.
                </Box>
            ) : (
                <Box sx={SUPPORT_TICKET_COMPOSER_SX}>
                    <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={5}
                        variant="outlined"
                        placeholder="Write a reply..."
                        value={replyContent}
                        onChange={(event) => onReplyChange(event.target.value)}
                        onKeyDown={handleKeyDown}
                        inputProps={{ "aria-label": "Reply to Ouro Support" }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={onSendReply}
                                        disabled={replying || !replyContent.trim()}
                                        aria-label="Send reply"
                                        sx={SUPPORT_TICKET_SEND_BUTTON_SX}
                                    >
                                        {replying ? (
                                            <CircularProgress size={18} color="inherit" />
                                        ) : (
                                            <SendRounded fontSize="small" />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={SUPPORT_TICKET_COMPOSER_FIELD_SX}
                    />
                </Box>
            )}
        </Box>
    );
}

SupportTicketSidebarChat.propTypes = {
    ticket: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.string,
    replyContent: PropTypes.string.isRequired,
    replying: PropTypes.bool,
    user: PropTypes.object,
    avatarCacheBust: PropTypes.number,
    onReplyChange: PropTypes.func.isRequired,
    onSendReply: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
};
